import clsx from "clsx";
import { memo, useEffect, useMemo, useRef, useState } from "react";
import { LuX } from "react-icons/lu";
import ActionButtons from "../../../../../components/Calculator/ActionButtons";
import OrdersList from "../../../../../components/Calculator/OrdersList";
import VesselSelection from "../../../../../components/Calculator/VesselSelection";
import { PieChart } from "../../../../../components/Charts/PieChart";
import CustomPagination from "../../../../../components/Common/CustomPagination";
import { initialAddFleet } from "../../../../../constants";
import CreateVesselModals from "../../../../../components/Calculator/CreateVesselModals";
import CustomAddModal from "../../../../../components/Common/CustomAddModal";
import AddNewOrder, { CargoAIModal } from "../../../Orders/AddNewOrder";
import ShowMap from "../../ShowMap";
import { GuideSpotlight, useGuidedTour } from "../../../../../common/GuidedTour";
import { hasGuideRequest, requestGuide, saveGuideProgress, takeGuideRequest } from "../../../../../common/guides";
import { useCalculatorController } from "../hooks/useCalculatorController";
import { useCalculatorStore } from "../hooks/useCalculatorStore";
import { calculationValidation } from "../utils/dataMapping";

const hasMatrixVessels = () => !!document.querySelector('[data-spotlight="matrix-vessel-row"]');

const VIEW_STEPS = [
  {
    target: '[data-spotlight="matrix-cargo-toggle"]',
    message: "Click Show Cargo List to open your cargos.",
  },
  {
    target: '[data-spotlight="matrix-cargo-item"]',
    message: "Pick a cargo from the list to load its vessels.",
  },
  {
    target: '[data-spotlight="matrix-vessel-table"]',
    message: () => (hasMatrixVessels()
      ? "The vessels you added from the vessel list appear here, one row each, with DWT, port, opening date, profit and freight cost."
      : "No vessels in the matrix yet. Click Add vessel: we'll take you to Vessel Manager to add one, or to turn on the Add to Matrix switch of one you already have."),
    action: () => (hasMatrixVessels() ? null : {
      label: "Add vessel",
      run: ({ tour, history }) => {
        tour.dismiss();
        saveGuideProgress("vessel", { matrixRequest: true });
        requestGuide("vessel");
        history.push("/vesselManager?tab=vesselList");
      },
    }),
    info: true,
    waitMs: 20000, // vessels load after the cargo is selected
  },
  {
    target: '[data-spotlight="matrix-vessel-row"]',
    message: "Click a vessel row to expand its voyage: ports, bunkers and expenses. Use the icons at the end of a row to remove or open it.",
    isDone: (el) => el.getAttribute("aria-expanded") === "true",
    skipWhen: () => !hasMatrixVessels(),
  },
  {
    target: '[data-spotlight="matrix-calculate-btn"]',
    message: "Click Calculate to refresh the results for every vessel. The charts and map below update with them.",
    info: true,
  },
];

const MatrixView = (props) => {
  const {
    setIsFullScreen = () => { },
    setLoader = () => { },
    loader = false,
  } = props;

  const { state, setSelectedVessel } = useCalculatorStore();
  const {
    loadInitialData,
    runCalculation,
    handlePortOperationChange,
    handleSelectedVesselChange,
    handleSetCalculatorData,
    handleAddOperation,
    handleDeleteOperation,
    handleDeleteFleet,
    handleRemoveOrderQuery,
    handleFetchPortDistance,
    handlePortCanalChange,
    handleReorderOperation,
    handlePortChange,
    handleQuerySelect,
    handleFleetSelect,
    setIsOverride,
  } = useCalculatorController({ setLoader });

  const [hideOrder, setHideOrder] = useState(true);

  // create cargo / vessel from the toolbar
  const [cargoAiOpen, setCargoAiOpen] = useState(false);
  const [newCargoOpen, setNewCargoOpen] = useState(false);
  const [newCargoQuery, setNewCargoQuery] = useState({});
  const [newCargoPorts, setNewCargoPorts] = useState([]);
  const [newVesselMode, setNewVesselMode] = useState(null);
  const newCargoRef = useRef();
  const onCreateCargo = (mode) => {
    setNewCargoQuery({});
    setNewCargoPorts([]);
    if (mode === "ai") setCargoAiOpen(true);
    else setNewCargoOpen(true);
  };
  const onCloseNewCargo = () => {
    setNewCargoOpen(false);
    setNewCargoQuery({});
    setNewCargoPorts([]);
  };

  // "Read the matrix" guide: pick a cargo, see its vessels, run the calculation.
  const viewTour = useGuidedTour({ guideKey: "view", steps: VIEW_STEPS, busy: Boolean(state.loader?.loading || loader) });
  const [viewPending, setViewPending] = useState(false);
  const mountedRef = useRef(true);
  useEffect(() => () => { mountedRef.current = false; }, []);
  // Start only once the page has finished loading its cargo list.
  useEffect(() => {
    if (!viewPending || state.loader?.loading || !state.allQueryFormat?.length) return;
    setViewPending(false);
    // no cleanup: setViewPending re-runs this effect, which would cancel the timer before it fires
    setTimeout(() => {
      if (!mountedRef.current || !takeGuideRequest("view")) return;
      viewTour.start(true);
    }, 500);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewPending, state.loader?.loading, state.allQueryFormat]);
  useEffect(() => {
    const run = () => {
      if (!hasGuideRequest("view")) return;
      setHideOrder(true); // the first step shows how to open the cargo list
      setViewPending(true);
    };
    run();
    window.addEventListener("guide:request", run);
    return () => window.removeEventListener("guide:request", run);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [pdfdata, setPdfData] = useState({});
  const [expandedRows, setExpandedRows] = useState({});
  const [showExpensesModal, setShowExpensesModal] = useState(false);
  const [voyagesDurationData, setVoyagesDurationData] = useState([]);
  const [voyagesExpensesBreakupData, setVoyagesExpensesBreakupData] = useState(
    [],
  );

  const [openAddOperation, setOpenAddOperation] = useState(false);
  const [openAddOperationIndex, setOpenAddOperationindex] = useState(0);
  const [marginModal, setMarginModal] = useState(false);
  const [supplyModal, setSupplyModal] = useState(false);
  const [addFleetModal, setAddFleetModal] = useState(false);
  const [splitHireModal, setSplitHireModal] = useState(false);
  const [selectedSplitHire, setSelectedSplitHire] = useState({});
  const [addFleetsArray, setAddFleetsArray] = useState([
    { ...initialAddFleet },
  ]);
  const [syncBunker, setSyncBunker] = useState(false);
  const [fleetCheckbox, setFleetCheckbox] = useState({
    ballast: { 1: true, 2: true, 3: true, 4: true },
    laden: { 1: true, 2: true, 3: true, 4: true },
  });

  const [hideCalculator, setHideCalculator] = useState({
    hideGraph: false,
    hideCalculator: false,
    hidePortList: false,
    hideMap: false,
  });

  const paginationOffset = state.pagination.offset;

  useEffect(() => {
    setIsFullScreen(true);
    loadInitialData(paginationOffset);
    return () => {
      setIsFullScreen(false);
    };
  }, [paginationOffset]);

  const calculationErrors = useMemo(() => {
    if (
      state.selectedVessel &&
      state.portOperation.length &&
      state.fleetList.length
    ) {
      return calculationValidation(state.portOperation, state.selectedVessel);
    }
    return [];
  }, [state.portOperation, state.selectedVessel, state.fleetList]);

  const calculatorDataProp = useMemo(
    () => ({
      clientQuery: state.selectedQuery,
      portOperation: state.portOperation,
      fleetList: state.fleetList,
    }),
    [state.selectedQuery, state.portOperation, state.fleetList],
  );

  useEffect(() => {
    const expandedId = Object.keys(expandedRows).find(
      (key) => expandedRows[key] === true,
    );
    let selectedVessel = null;

    if (expandedId) {
      selectedVessel = (state.fleetList || []).find(
        (v) => v._id?.toString() === expandedId?.toString(),
      );
    }

    if (!selectedVessel && state.selectedVessel && state.selectedVessel._id) {
      selectedVessel =
        (state.fleetList || []).find(
          (v) => v._id?.toString() === state.selectedVessel._id?.toString(),
        ) || state.selectedVessel;
    }

    if (!selectedVessel && state.fleetList && state.fleetList.length > 0) {
      selectedVessel = state.fleetList[0];
    }

    if (!selectedVessel) {
      setVoyagesDurationData([]);
      setVoyagesExpensesBreakupData([]);
      return;
    }

    const portStayTotal = Number(selectedVessel?.portStayTotal || 0);
    const ballastSeaDuration = Number(selectedVessel?.ballastSeaDuration || 0);
    const ladenSeaDuration = Number(selectedVessel?.ladenSeaDuration || 0);

    if (portStayTotal > 0 || ballastSeaDuration > 0 || ladenSeaDuration > 0) {
      setVoyagesDurationData([
        { label: "Port Stay Total", value: portStayTotal },
        { label: "Ballast Sea Duration", value: ballastSeaDuration },
        { label: "Laden Sea Duration", value: ladenSeaDuration },
      ]);
    } else {
      setVoyagesDurationData([]);
    }

    let totalPortExpenses = 0;
    (state.portOperation || []).forEach((op) => {
      totalPortExpenses += op?.portExp ? Number(op.portExp) : 0;
    });

    const netFrtRevenue = Number(selectedVessel?.netFrtRevenue || 0);
    const miscRevenue = Number(state.selectedQuery?.miscRevenue || 0);
    const profitNet = Number(selectedVessel?.profitNet || 0);
    const profitNetAbs = Math.abs(profitNet);

    const totalExpenses = netFrtRevenue + miscRevenue - profitNetAbs;
    const fuelExpense = Number(selectedVessel?.fuelExpense || 0);
    const hireExpense = Number(selectedVessel?.hireExpense || 0);

    if (fuelExpense !== 0 || hireExpense !== 0 || totalPortExpenses !== 0) {
      const fuelExpAbs = Math.abs(fuelExpense);
      const hireExpAbs = Math.abs(hireExpense);
      const portExpAbs = Math.abs(totalPortExpenses);
      const otherExp = Math.max(
        0,
        totalExpenses - fuelExpAbs - hireExpAbs - portExpAbs,
      );

      setVoyagesExpensesBreakupData([
        { label: "Fuel Expenses", value: fuelExpAbs },
        { label: "Hire Expenses", value: hireExpAbs },
        { label: "Total Port Expenses", value: portExpAbs },
        { label: "Others Expenses", value: otherExp },
      ]);
    } else {
      setVoyagesExpensesBreakupData([]);
    }
  }, [
    state.fleetList,
    state.portOperation,
    state.selectedQuery,
    state.selectedVessel,
    expandedRows,
  ]);

  const handlePagination = (action) => {
    let newPage = state.pagination.offset;
    if (action === "first") newPage = 1;
    else if (action === "dec" && state.pagination.offset > 1) newPage -= 1;
    else if (
      action === "inc" &&
      state.pagination.offset < state.pagination.totalPages
    )
      newPage += 1;
    else if (action === "last") newPage = state.pagination.totalPages;

    if (newPage !== state.pagination.offset) {
      loadInitialData(newPage);
    }
  };

  const onSetHideCalculator = (type) => {
    const newState = { ...hideCalculator, [type]: !hideCalculator[type] };
    if (Object.values(newState).filter((v) => v === false).length === 0) return;
    setHideCalculator(newState);
  };

  return (
    <div className="calculator-section flex flex-col w-full min-h-[calc(100vh-60px)]">
      <div className="flex flex-row items-start w-full min-w-0">
        {!hideOrder && (
        <>
        {/* Left Sidebar - Cargo Orders List */}
        <div
          className={`calc-order-section w-[210px] sm:w-[220px] shrink-0 bg-white border-r border-gray-300 rounded-none p-2.5 flex flex-col h-[calc(100vh-56px)] max-h-[calc(100vh-56px)] sticky top-[56px] self-start z-[99] slideInRightMedium second-sidebar-box ${hideOrder ? "" : "second-sidebar-box-active"
            }`}
        >
          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between pb-1.5 border-b border-gray-200 shrink-0">
              <div className="flex items-center gap-1.5 text-[11px] font-medium text-gray-800 uppercase tracking-wider">
                <span>Select a Cargo</span>
              </div>
              <button
                onClick={() => setHideOrder(true)}
                className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-none transition-colors"
                title="Close Cargo Sidebar"
              >
                <LuX className="w-3.5 h-3.5" />
              </button>
            </div>
            <OrdersList
              onRemoveOrderQuery={(id) => handleRemoveOrderQuery(id)}
              allQueryFormat={state.allQueryFormat}
              selectedQuery={state.selectedQuery}
              setSelectedQuery={handleQuerySelect}
              onChangeSelectedQuery={(query) => handleQuerySelect(query)}
              setHideOrder={setHideOrder}
            />
          </div>
        </div>

        <span
          className="trigger-close-sidebar"
          onClick={() => setHideOrder(true)}
        ></span>
        </>
        )}

        {/* Right Main Content */}
        <div
          className={clsx(
            "flex flex-col main-content-wrapper-inner min-w-0 overflow-hidden flex-1"
          )}
        >
          {/* Action Bar */}
          <div>
            <ActionButtons
              IsQuerySelected={Boolean(state.selectedQuery?._id)}
              hideOrder={hideOrder}
              isExpanded={Object.values(expandedRows)[0] === true}
              setHideOrder={setHideOrder}
              onSetOpenMap={() => {
                if (state.selectedQuery?._id) {
                  window.open(`/map/${state.selectedQuery?._id}`);
                }
              }}
              onClickCalculate={() => runCalculation(false)}
              activeSection={hideCalculator}
              onSetHideCalculator={onSetHideCalculator}
              onClickCalculateAndSave={() => runCalculation(true)}
              pdfdata={pdfdata}
              isFromMatrix
              onCreateCargo={onCreateCargo}
              onCreateVessel={setNewVesselMode}
            />
          </div>

          {/* Body Section */}
          <div>
            {!state.selectedQuery?._id ? (
              <div className="h-[calc(100vh-105px)]">
                <div className="flex h-full justify-center items-center">
                  <p className="m-a text-xl">Please select a query.</p>
                </div>
              </div>
            ) : (
              <div className="calc-secound-section px-1 pt-1">
                  <div className="bottom-section-grid">
                  {state.pagination.totalItems > state.pagination.limit && (
                    <div className="pb-20">
                      <CustomPagination
                        onChangePagination={handlePagination}
                        filters={state.pagination}
                        totalPage={state.pagination.totalItems}
                      />
                    </div>
                  )}

                  <VesselSelection
                    selectedVessels={state.selectedVessel}
                    onFetchPortOperationDistance={(
                      reset,
                      propCalc,
                      checks,
                      isSave,
                    ) =>
                      handleFetchPortDistance(reset, propCalc, checks, isSave)
                    }
                    selectedQuery={state.selectedQuery}
                    onChangeFleetRowSelect={(fleet) => handleFleetSelect(fleet)}
                    onDeleteFleet={(id) => handleDeleteFleet(id)}
                    onClickAddFleet={() => setAddFleetModal((prev) => !prev)}
                    addFleetModal={addFleetModal}
                    addFleetsArray={addFleetsArray}
                    setAddFleetsArray={setAddFleetsArray}
                    calculatorData={calculatorDataProp}
                    setCalculatorData={handleSetCalculatorData}
                    setLoader={setLoader}
                    loader={loader}
                    setAddFleetModal={setAddFleetModal}
                    sendCalculationRequest={(calcData, autoSave) =>
                      runCalculation(autoSave, calcData ? { ...state, fleetList: calcData.fleetList } : undefined)
                    }
                    setSelectedVessels={setSelectedVessel}
                    splitHireModal={splitHireModal}
                    setSplitHireModal={setSplitHireModal}
                    selectedSplitHire={selectedSplitHire}
                    setSelectedSplitHire={setSelectedSplitHire}
                    openSplitHireModal={(fleet) => {
                      setSelectedSplitHire(fleet);
                      setSplitHireModal(true);
                    }}
                    getInitalQueryAndFleetData={() => loadInitialData(1)}
                    initialFleetList={state.fleetList}
                    onChangeSelectedVessel={handleSelectedVesselChange}
                    fleetCheckbox={fleetCheckbox}
                    setFleetCheckbox={setFleetCheckbox}
                    setMarginModal={setMarginModal}
                    syncBunker={syncBunker}
                    setSyncBunker={setSyncBunker}
                    setSupplyModal={setSupplyModal}
                    cargoTabs={{
                      list: state.allQueryFormat || [],
                      selectedId: state.selectedQuery?._id,
                      onSelect: handleQuerySelect,
                    }}
                    onChangeSyncBunker={(checked) => {
                      setSyncBunker(checked);
                      handleSetCalculatorData((current) => ({
                        ...current,
                        clientQuery: {
                          ...current.clientQuery,
                          fuelPriceOverridden: checked,
                          fuelCalculationMethod: checked ? "LIFO" : "DEFAULT",
                        },
                      }));
                    }}
                    isOverride={state.isOverride}
                    calculationErrors={calculationErrors}
                    marginModal={marginModal}
                    onChangePortOperations={handlePortOperationChange}
                    onAddOperations={(data) =>
                      handleAddOperation(data, openAddOperationIndex)
                    }
                    onDeleteOperations={(index, id) =>
                      handleDeleteOperation(index, id)
                    }
                    onReorder={handleReorderOperation}
                    onPortChange={handlePortChange}
                    supplyModal={supplyModal}
                    fetchPortDistance={{ current: false }}
                    openAddOperation={openAddOperation}
                    setOpenAddOperation={setOpenAddOperation}
                    setOpenAddOperationindex={setOpenAddOperationindex}
                    onChangePortCanalInQueryState={handlePortCanalChange}
                    setIsOverride={setIsOverride}
                    hideOrder={hideOrder}
                    setHideOrder={setHideOrder}
                    hideCalculator={hideCalculator}
                    getCurrentResult={state.calculationResult}
                    setPdfData={setPdfData}
                    onChangeChartValue={() => { }}
                    expandedRows={expandedRows}
                    setExpandedRows={setExpandedRows}
                    showExpensesModal={showExpensesModal}
                    setShowExpensesModal={setShowExpensesModal}
                  />
                </div>

                {!hideCalculator.hideGraph && (
                  <div className="flex flex-wrap">
                    <div className="w-full md:w-[50%]">
                      {voyagesDurationData.length > 0 && (
                        <PieChart
                          data={voyagesDurationData}
                          label="Voyage Duration Break-up"
                        />
                      )}
                    </div>
                    <div className="w-full md:w-[50%] pr-2">
                      {voyagesExpensesBreakupData.length > 0 && (
                        <PieChart
                          data={voyagesExpensesBreakupData}
                          label="Voyage Expenses Break-up"
                        />
                      )}
                    </div>
                  </div>
                )}

                {!hideCalculator.hideMap &&
                  state.selectedQuery?._id &&
                  state.selectedQuery?.portToPortDistance?.output &&
                  Object.keys(
                    state.selectedQuery?.portToPortDistance?.output || {},
                  ).length > 0 && (
                    <div className="flex justify-center items-center py-4">
                      <div className="h-[500px] w-full overflow-hidden px-2">
                        <ShowMap
                          showSidebar={false}
                          setIsFullScreen={setIsFullScreen}
                          id={state.selectedQuery._id}
                          portDistanceResponse={
                            state.selectedQuery.portToPortDistance.output
                          }
                          portOperation={state.portOperation}
                          fleetList={state.fleetList}
                        />
                      </div>
                    </div>
                  )}
              </div>
            )}
          </div>
        </div>
      </div>
      <GuideSpotlight tour={viewTour} />

      <CargoAIModal
        open={cargoAiOpen}
        handleClose={() => setCargoAiOpen(false)}
        setQuery={setNewCargoQuery}
        setPortOperations={setNewCargoPorts}
        onFilled={() => {
          setCargoAiOpen(false);
          setNewCargoOpen(true);
        }}
      />
      <CustomAddModal
        open={newCargoOpen}
        title="New Cargo"
        maxWidth="lg"
        saveLabel="Create"
        buttonCase="capitalize"
        handleClose={onCloseNewCargo}
        onClose={onCloseNewCargo}
        onSubmitConfirm={() => newCargoRef.current?.onSubmitQuery()}
      >
        <AddNewOrder
          hideCreateAI
          isOpen={newCargoOpen}
          ref={newCargoRef}
          setLoader={setLoader}
          loader={loader}
          onCloseModal={onCloseNewCargo}
          selectedQuery={newCargoQuery}
          selectedPorts={newCargoPorts}
          selectedPortsCommence={[]}
          isEdit={false}
          handleActionComplete={(action) => {
            if (action === "created") loadInitialData(1);
          }}
        />
      </CustomAddModal>
      <CreateVesselModals
        mode={newVesselMode}
        onClose={() => setNewVesselMode(null)}
        setLoader={setLoader}
        onCreated={() => loadInitialData(1)}
      />
    </div>
  );
};

export default memo(MatrixView);
